import React, { useState, useEffect, useCallback } from 'react';
import { Cloud, Check, AlertCircle, RefreshCw } from 'lucide-react';
import { sans, useTheme } from '../tokens';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const FY = 2026;

function formatRelative(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return `${Math.floor(diffH / 24)}d ago`;
}

// Top-bar button that pulls Sage Intacct data into Supabase. Affects multiple
// tabs (Close & Review, Bank Feed, FP&A) so it lives at the app level, not
// inside any one tab. Calls the convenience wrapper that does COA + GL for
// every closed FY month in one shot.
export default function SageSyncButton({ companyId }) {
  const t = useTheme();
  const [state, setState] = useState('idle');
  const [lastSync, setLastSync] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [resultMsg, setResultMsg] = useState(null);

  const fetchLastSync = useCallback(() => {
    if (!companyId) return;
    fetch(`${BACKEND_URL}/api/sage/sync/log?company_id=${companyId}&limit=1`)
      .then(r => (r.ok ? r.json() : null))
      .then(rows => {
        if (Array.isArray(rows) && rows[0]?.completed_at) {
          setLastSync(rows[0].completed_at);
        }
      })
      .catch(() => {});
  }, [companyId]);

  useEffect(() => { fetchLastSync(); }, [fetchLastSync]);

  const handleSync = async () => {
    if (state === 'loading') return;
    setState('loading');
    setErrorMsg(null);
    setResultMsg(null);
    try {
      const r = await fetch(
        `${BACKEND_URL}/budget-vs-forecast/sync-ytd?fy=${FY}`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const text = await r.text();
        throw new Error(`HTTP ${r.status}: ${text.slice(0, 160)}`);
      }
      const body = await r.json();
      const months = body.months_synced?.length || 0;
      const rows = body.rows_synced || 0;
      setResultMsg(`Synced ${rows.toLocaleString()} GL rows across ${months} month${months === 1 ? '' : 's'} (FY${FY})`);
      setLastSync(new Date().toISOString());
      setState('success');
      // Notify the rest of the app so tabs that cache GL data can refresh.
      window.dispatchEvent(new CustomEvent('finkloz-sage-sync-completed', { detail: body }));
      setTimeout(() => setState('idle'), 2800);
    } catch (e) {
      setErrorMsg(e.message || 'Sync failed');
      setState('error');
      setTimeout(() => setState('idle'), 5000);
    }
  };

  let Icon = Cloud;
  let label = 'Sync Sage';
  let color = t.textSecondary;
  let title = `Pulls latest Sage Intacct GL data into FinKloz (Close & Review, Bank Feed, FP&A all read from this).${lastSync ? `\nLast sync ${formatRelative(lastSync)}` : ''}`;

  if (state === 'loading') {
    Icon = RefreshCw;
    label = 'Syncing…';
  } else if (state === 'success') {
    Icon = Check;
    label = 'Synced';
    color = '#1D9E75';
    title = resultMsg || title;
  } else if (state === 'error') {
    Icon = AlertCircle;
    label = 'Sync failed';
    color = '#C0392B';
    title = errorMsg || 'Sync failed';
  }

  const rel = state === 'idle' ? formatRelative(lastSync) : null;

  return (
    <button
      onClick={handleSync}
      title={title}
      disabled={state === 'loading'}
      style={{
        border: `1px solid ${t.border}`,
        background: t.inputBg,
        borderRadius: 8,
        padding: '6px 12px',
        cursor: state === 'loading' ? 'wait' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 500,
        fontFamily: sans,
        color,
        opacity: state === 'loading' ? 0.7 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon
        size={13}
        style={{
          animation: state === 'loading' ? 'finkloz-spin 1s linear infinite' : 'none',
        }}
      />
      <span>{label}</span>
      {rel && (
        <span style={{ color: t.textMuted, fontSize: 9, marginLeft: 2 }}>
          · {rel}
        </span>
      )}
    </button>
  );
}
